import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { schoolService } from "../services/schoolService";
import { inviteEmailError } from "../lib/email";

// Rank order matches the backend ladder (invite.service.ts SCHOOL_ROLE_RANK).
// Labels mirror the School-Admin invite modal so the same role reads the same
// way in both apps — CLASSROOM_VIEWER is shown to humans as "Teacher".
const ROLE_OPTIONS = [
  { value: "OWNER", label: "Owner" },
  { value: "BILLING", label: "Billing manager" },
  { value: "ADMIN", label: "Admin" },
  { value: "COUNSELOR", label: "Counselor" },
  { value: "CLASSROOM_VIEWER", label: "Teacher" },
];

const roleLabel = (value) => ROLE_OPTIONS.find((r) => r.value === value)?.label ?? value;

const displayName = (m) => {
  const name = `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim();
  return name || m.email;
};

/**
 * School staff roster for the Platform-Admin school-detail modal.
 *
 * The school-detail view historically showed a single "Admin Name" sourced
 * from School.schoolAdminId — a legacy one-to-one field. Schools can have
 * many staff (SchoolMembership rows with a role ladder), so this panel lists
 * them all and lets a platform admin add another admin, change ranks, or
 * deactivate someone without going through the school's own admin.
 *
 * Every endpoint used here already existed and is admin-gated server-side;
 * this only surfaces them.
 */
export default function SchoolStaffPanel({ schoolId, schoolName }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteTitle, setInviteTitle] = useState("");
  const [inviteRole, setInviteRole] = useState("ADMIN");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setLoadError("");
    try {
      const [rows, history] = await Promise.all([
        schoolService.getSchoolMemberships(schoolId),
        schoolService.getInvites({ schoolId, userRole: "SCHOOL_ADMIN" }),
      ]);
      setMembers(Array.isArray(rows) ? rows : []);
      setInvites(Array.isArray(history) ? history : []);
    } catch (err) {
      setLoadError(err?.message || "Couldn't load school staff and invitations");
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    void load();
  }, [load]);

  const changeRole = async (membership, role) => {
    if (role === membership.role) return;
    setBusyId(membership.id);
    try {
      await schoolService.updateSchoolMembershipRole(schoolId, membership.id, role);
      toast.success(`${displayName(membership)} is now ${roleLabel(role)}`);
      await load();
    } catch (err) {
      // Backend guards the last-owner case; surface its message verbatim.
      toast.error(err?.message || "Couldn't change role");
    } finally {
      setBusyId(null);
    }
  };

  const toggleStatus = async (membership) => {
    const next = membership.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setBusyId(membership.id);
    try {
      await schoolService.updateSchoolMembershipStatus(schoolId, membership.id, next);
      toast.success(`${displayName(membership)} ${next === "ACTIVE" ? "reactivated" : "deactivated"}`);
      await load();
    } catch (err) {
      toast.error(err?.message || "Couldn't update status");
    } finally {
      setBusyId(null);
    }
  };

  const emailError = inviteEmailError(inviteEmail);

  const sendInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email || emailError) return;
    setInviting(true);
    try {
      const response = await schoolService.inviteSchoolStaff(schoolId, {
        email,
        role: inviteRole,
        name: inviteName.trim() || undefined,
        title: inviteTitle.trim() || undefined,
      });
      if (!response.success) throw new Error(response.error?.detail || response.message || "Couldn't send invite");
      if (response.data?.invites?.some((invite) => invite.emailFailedAt)) {
        toast.warning(`Invitation saved for ${email}, but the email failed to send.`);
      } else {
        toast.success(`Invitation created for ${email}`);
      }
      setInviteEmail("");
      setInviteName("");
      setInviteTitle("");
      setShowInvite(false);
      await load();
    } catch (err) {
      toast.error(err?.message || "Couldn't send invite");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-gray-900 text-base font-semibold">
          School staff{members.length ? ` (${members.length})` : ""}
        </span>
        <button
          type="button"
          onClick={() => setShowInvite((v) => !v)}
          className="px-3 py-1.5 border border-blue-500 rounded-md text-sm font-medium text-gray-900 hover:bg-blue-50 transition-colors"
        >
          {showInvite ? "Cancel" : "Add another admin"}
        </button>
      </div>

      <p className="text-sm text-gray-600">Add more admins to this school here. Each person receives their own invitation; you only need one school record.</p>
      <button type="button" disabled={loading} onClick={load} className="self-start text-sm text-blue-600 hover:underline">Refresh staff & invitations</button>
      {showInvite && (
        <form onSubmit={sendInvite} className="flex flex-col gap-2 rounded-lg border border-blue-100 bg-blue-50/40 p-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Email address"
                className={`px-3 py-2 rounded-lg border text-sm focus:outline-none ${
                  emailError ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-blue-500"
                }`}
              />
              {emailError && <p className="text-xs text-red-500">{emailError}</p>}
            </div>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:border-blue-500"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <input
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Name (optional)"
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
            />
            <input
              value={inviteTitle}
              onChange={(e) => setInviteTitle(e.target.value)}
              placeholder="Title (optional)"
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim() || !!emailError}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-colors"
            >
              {inviting ? "Sending…" : `Send invite to ${schoolName || "this school"}`}
            </button>
          </div>
        </form>
      )}

      {loadError && <p role="alert" className="text-sm text-red-600">{loadError}. Refresh to try again.</p>}
      {loading ? (
        <p className="text-sm text-gray-500">Loading staff…</p>
      ) : loadError ? null : members.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No staff yet — the invited admin becomes a member once they accept.
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{displayName(m)}</p>
                <p className="truncate text-xs text-gray-500">
                  {m.email}
                  {m.title ? ` · ${m.title}` : ""}
                </p>
              </div>
              <select
                value={m.role}
                disabled={busyId === m.id}
                onChange={(e) => changeRole(m, e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none focus:border-blue-500 disabled:opacity-50"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={busyId === m.id}
                onClick={() => toggleStatus(m)}
                title={m.status === "ACTIVE" ? "Deactivate this staff member" : "Reactivate this staff member"}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                  m.status === "ACTIVE"
                    ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {m.status === "ACTIVE" ? "Active" : "Inactive"}
              </button>
            </div>
          ))}
        </div>
      )}
      {!loading && !loadError && (
        <section aria-label="Admin invitation history" className="flex flex-col gap-2">
          <h3 className="font-semibold">Admin invitation history ({invites.length})</h3>
          {invites.length === 0 && <p className="text-sm text-gray-500">No admin invitations yet.</p>}
          {invites.map((invite) => {
            const status = invite.status === "PENDING" && new Date(invite.expiresAt).getTime() <= Date.now() ? "EXPIRED" : invite.status;
            return <div key={invite.id} className="rounded-lg border border-gray-200 p-3 text-sm">
              <div className="flex justify-between gap-2"><span className="font-medium break-all">{invite.name || invite.email}</span><span>{status === "PENDING" ? "Awaiting acceptance" : status.charAt(0) + status.slice(1).toLowerCase()}</span></div>
              {invite.name && <p className="break-all text-gray-600">{invite.email}</p>}
              <p className="text-gray-600">{roleLabel(invite.schoolRole || "ADMIN")}{invite.title ? ` · ${invite.title}` : ""}</p>
              <p className="text-xs text-gray-500">Invited {new Date(invite.createdAt).toLocaleString()}</p>
              {status === "PENDING" && <p className="text-xs text-gray-500">Expires {new Date(invite.expiresAt).toLocaleString()}</p>}
              {invite.emailFailedAt ? <p className="text-xs text-red-600">Email failed to send. Invitation saved.</p> : invite.emailSentAt ? <p className="text-xs text-gray-500">Email sent {new Date(invite.emailSentAt).toLocaleString()}</p> : <p className="text-xs text-gray-500">Email delivery not recorded</p>}
            </div>;
          })}
        </section>
      )}
    </div>
  );
}
